import React, { useState, useMemo, useEffect, useCallback, useRef, memo } from 'react';
import { Group, Rect, Text } from 'react-konva';
import {
  getAnnotationDisplayText,
} from '../utils/annotationUtils';
import { darkenColor, createStripePattern } from '../utils/patternUtils';
import useAnnotationResize from '../hooks/useAnnotationResize';
import useAnnotationDrag from '../hooks/useAnnotationDrag';
import { DeleteButton, ResizeHandle } from './AnnotationControls';
import {
  DEFAULT_PIXELS_PER_BEAT,
  DEFAULT_BLOCK_HEIGHT,
  DEFAULT_FONT_SIZE,
  DEFAULT_STROKE_WIDTH,
  COLORS
} from '../utils/constants';
import { useViewMode } from '../contexts/ViewModeContext';
import {
  getAnnotationBlockInteraction,
  isPrimaryBlockPointerAction,
  planPointerDrag,
  shouldHandleBlockClick,
} from '../utils/annotationBlockInteraction';

/**
 * AnnotationBlock component - refactored implementation with performance optimizations
 */
const AnnotationBlock = memo(({
  annotation,
  pixelsPerBeat = DEFAULT_PIXELS_PER_BEAT,
  beatsPerMeasureMap = [],
  calculatePosition,
  height = DEFAULT_BLOCK_HEIGHT,
  isSelected = false,
  isCopied = false,
  isPasted = false,
  toolMode = 'pencil',
  interactionDisabled = false,
  canOpenForm,
  canSelectBlock,
  previewOffsetX = 0,
  collapsed = false,
  onSelect = () => {},
  onDragMove = (pos) => {},
  onResize = () => {},
  onDoubleClick = (e) => {},
  onDelete = () => {},
  onCursorMarqueeStart = () => {},
  onCursorBlockClick = () => {},
  onContextMenu = () => {},
  onGroupDragStart = () => {},
  onGroupDragMove = () => {},
  onGroupDragEnd = () => {},
  otherAnnotations = [],
  x = null,
  scrollPosition = 0,
  trackColor,
  currentGridLevel,
  actualPixelsPerBeat,
  totalWidth,
  timeSignatures,
  isMarkedForDeletion = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [stripePattern, setStripePattern] = useState(null);
  const [tempResize, setTempResize] = useState(null); // { width, direction }
  const [previewDragX, setPreviewDragX] = useState(null);
  const singleDragOriginClientXRef = useRef(null);
  const suppressSecondaryClickRef = useRef(false);
  const formOpeningAllowed = canOpenForm ?? !interactionDisabled;
  const blockSelectionAllowed = canSelectBlock ?? !interactionDisabled;

  // Check if the annotation is complete
  const completed = annotation.formData?.completed === true;
  
  // View mode
  const {isViewMode} = useViewMode();

  // Calculate position and dimensions based on musical time
  const { startX: computedStartX, width: calculatedWidth } = useMemo(() => {
    // If x is provided directly, use that instead of calculating
    const startX = x !== null ? x : (
      calculatePosition ? 
        calculatePosition(annotation.startMeasure, annotation.startBeat, annotation.startSubdivision || 0) :
        0
    );
    
    const endX = calculatePosition ? 
      calculatePosition(annotation.endMeasure, annotation.endBeat, annotation.endSubdivision || 0) :
      0;
    
    // If x is provided directly, we need to ensure we don't subtract scrolling adjustment from width
    const width = x !== null ? 
      endX - calculatePosition(annotation.startMeasure, annotation.startBeat, annotation.startSubdivision || 0) :
      endX - startX;

    return { startX, width };
  }, [annotation, calculatePosition, x]);
  const startX = tempResize?.direction === 'left'
    ? computedStartX + (calculatedWidth - tempResize.width)
    : computedStartX;
  const renderX = (previewDragX !== null ? previewDragX : startX) + previewOffsetX;

  const finalWidth = tempResize?.width ?? calculatedWidth;
  // Determine block color based on track type - memoized with stable dependencies
  const getBlockColor = useCallback(() => {
    if (trackColor) return trackColor;
  
    // Find the track for this annotation
    const trackId = annotation.trackId;
    
    // Get all tracks from window.trackData (set in AnnotationSheet.js)
    const tracks = window.trackData || [];
    
    // Find the index of the track in the tracks array
    const trackIndex = tracks.findIndex(t => t.id === trackId);
    
    // If track is found, use the corresponding Bar Level Annotation color
    if (trackIndex !== -1) {
      return COLORS.BAR_LEVEL_COLORS[trackIndex % COLORS.BAR_LEVEL_COLORS.length];
    }
    
    // Fallback to previous color logic if track not found
    
    // First check if the annotation itself has a colorType
    if (annotation.colorType) {
      if (annotation.colorType === 'chord') return COLORS.TRACK_CHORD;
      if (annotation.colorType === 'piano') return COLORS.TRACK_PIANO;
      if (annotation.colorType === 'pad') return COLORS.TRACK_PAD;
      if (annotation.colorType === 'drum') return COLORS.TRACK_DRUM;
    }
    
    // Check if other annotations from the same track have a colorType
    if (otherAnnotations && otherAnnotations.length > 0) {
      // Get the first annotation from the same track to find the track's color
      const sameTrackAnnotation = otherAnnotations.find(a => a.trackId === trackId);
      if (sameTrackAnnotation && sameTrackAnnotation.colorType) {
        if (sameTrackAnnotation.colorType === 'chord') return COLORS.TRACK_CHORD;
        if (sameTrackAnnotation.colorType === 'piano') return COLORS.TRACK_PIANO;
        if (sameTrackAnnotation.colorType === 'pad') return COLORS.TRACK_PAD;
        if (sameTrackAnnotation.colorType === 'drum') return COLORS.TRACK_DRUM;
      }
    }
    
    // If no colorType found, use the trackId to determine color
    if (trackId === 'chord-track') return COLORS.TRACK_CHORD;
    if (trackId === 'piano-track') return COLORS.TRACK_PIANO;
    if (trackId === 'pad-track') return COLORS.TRACK_PAD;
    if (trackId === 'drum-track') return COLORS.TRACK_DRUM;
    
    // For any other track, check if it contains certain keywords to determine color
    const trackIdLower = trackId.toLowerCase();
    if (trackIdLower === 'chord-track') return COLORS.TRACK_CHORD;
    if (trackIdLower.includes('piano')) return COLORS.TRACK_PIANO;
    if (trackIdLower.includes('pad')) return COLORS.TRACK_PAD;
    if (trackIdLower.includes('drum')) return COLORS.TRACK_DRUM;
    
    return COLORS.TRACK_PAD; // Default color
  }, [trackColor, annotation.trackId, annotation.colorType, otherAnnotations, isMarkedForDeletion]);

  // Use custom hook for drag behavior
  const { 
    isDragging, 
    setIsDragging, 
    dragBoundFunc,
  } = useAnnotationDrag({ 
    annotation, 
    otherAnnotations,
    calculatePosition,
    scrollPosition,
    pixelsPerBeat,
    totalWidth,
    beatsPerMeasureMap
  });

  // Use custom hook for resize behavior
  const { 
    isResizing, 
    startResizing 
  } = useAnnotationResize({
    annotation,
    timeSignatures,
    startX: computedStartX,
    pixelsPerBeat,
    otherAnnotations,
    calculatePosition,
    scrollPosition: scrollPosition,
    onResizePreview: setTempResize,
    onResizeCommit: ({ width, direction }) => {
      setTempResize(null);
      onResize({ width, direction });
    },
    totalWidth,
    onSelect,
    beatsPerMeasureMap,
    currentGridLevel,
    movementStep: actualPixelsPerBeat / 4,
  });

  const interaction = getAnnotationBlockInteraction({
    toolMode,
    isSelected,
    isViewMode,
    isResizing,
    disabled: interactionDisabled,
  });
  const resizeEnabled = (
    !isViewMode &&
    !interactionDisabled &&
    !isMarkedForDeletion &&
    (toolMode === 'pencil' || toolMode === 'cursor')
  );

  // Create stripe pattern for completed annotations
  useEffect(() => {
    if (completed) {
      const baseColor = getBlockColor();
      const pattern = createStripePattern(baseColor, 16, 1, 45);
      setStripePattern(pattern);
    } else {
      setStripePattern(null);
    }
  }, [completed, getBlockColor]);

  // If collapsed, return null
  if (collapsed) {
    return null;
  }
  
  // Get display text
  const displayText = getAnnotationDisplayText(annotation);

  return (
    <Group
      x={renderX}
      y={0}
      draggable={interaction === 'single-drag' || interaction === 'group-drag'}
      dragBoundFunc={(pos) => (
        interaction === 'single-drag' ? dragBoundFunc(pos) : { x: pos.x, y: 0 }
      )}
      onDragStart={(e) => {
        if (!isPrimaryBlockPointerAction({
          button: e.evt.button,
          ctrlKey: e.evt.ctrlKey,
          platform: navigator.platform,
        })) return;
        if (interaction === 'group-drag') {
          setIsDragging(true);
          onGroupDragStart({ annotation, clientX: e.evt.clientX, renderX });
          e.target.getStage().container().style.cursor = 'grabbing';
          return;
        }
        if (interaction !== 'single-drag') return;
        singleDragOriginClientXRef.current = e.evt.clientX;
        onSelect();
        setIsDragging(true);
        e.target.getStage().container().style.cursor = 'grabbing';
      }}
      onMouseDown={(e) => {
        const isPrimaryPointer = isPrimaryBlockPointerAction({
          button: e.evt.button,
          ctrlKey: e.evt.ctrlKey,
          platform: navigator.platform,
        });
        suppressSecondaryClickRef.current = !isPrimaryPointer;
        if (!isPrimaryPointer) {
          e.cancelBubble = true;
          return;
        }
        if (interaction === 'marquee') {
          e.cancelBubble = true;
          onCursorMarqueeStart(e, annotation);
          return;
        }
        if (interaction === 'group-drag') e.cancelBubble = true;
        const isHandle = e.target.name() === 'resize-handle';
        if (!isHandle && interaction !== 'none') {
          e.target.getStage().container().style.cursor = 'grabbing';
        }
      }}
      onContextMenu={(e) => {
        if (toolMode !== 'cursor' || isViewMode || interactionDisabled) return;
        e.evt.preventDefault();
        e.cancelBubble = true;
        suppressSecondaryClickRef.current = true;
        onContextMenu(e, annotation);
      }}
      onMouseOver={(e) => {
        const isHandle = e.target.name() === 'resize-handle';
        if (interaction === 'marquee') {
          e.target.getStage().container().style.cursor = 'crosshair';
        } else if (!isHandle && interaction !== 'none') {
          e.target.getStage().container().style.cursor = 'grab';
        } else if (isHandle && resizeEnabled) {
          e.target.getStage().container().style.cursor = 'ew-resize';
        }
      }}
      onMouseOut={(e) => {
        e.target.getStage().container().style.cursor = 'default';
      }}
      onDragMove={(e) => {
        if (interaction === 'group-drag') {
          const delta = e.target.x() - renderX;
          e.target.x(renderX);
          onGroupDragMove({ annotation, delta, clientX: e.evt.clientX });
          return;
        }
        if (interaction !== 'single-drag') return;
        const preview = planPointerDrag({
          originClientX: singleDragOriginClientXRef.current,
          clientX: e.evt.clientX,
          originX: computedStartX,
          movementStep: actualPixelsPerBeat / 4,
          clampX: value => dragBoundFunc({ x: value }).x,
        });
        e.target.x(preview.x);
        setPreviewDragX(preview.x);
      }}
      onDragEnd={(e) => {
        if (interaction === 'group-drag') {
          const delta = e.target.x() - renderX;
          e.target.x(renderX);
          setIsDragging(false);
          onGroupDragEnd({ annotation, delta, clientX: e.evt.clientX });
          e.target.getStage().container().style.cursor = 'grab';
          return;
        }
        if (interaction !== 'single-drag') return;
        
        // Get current position (this is in scroll-adjusted coordinates)
        const result = planPointerDrag({
          originClientX: singleDragOriginClientXRef.current,
          clientX: e.evt.clientX,
          originX: computedStartX,
          movementStep: actualPixelsPerBeat / 4,
          clampX: value => dragBoundFunc({ x: value }).x,
        });
        singleDragOriginClientXRef.current = null;
        e.target.x(computedStartX);
        setPreviewDragX(null);
        onDragMove({
          proposedDelta: result.rawDelta,
        });
        
        setIsDragging(false);
        e.target.getStage().container().style.cursor = 'grab';
        onSelect();
      }}
      onClick={(e) => {
        e.cancelBubble = true;
        const shouldHandle = shouldHandleBlockClick({
          suppressSecondaryClick: suppressSecondaryClickRef.current,
          button: e.evt.button,
          ctrlKey: e.evt.ctrlKey,
          platform: navigator.platform,
        });
        suppressSecondaryClickRef.current = false;
        if (!shouldHandle) return;
        if (interactionDisabled) {
          if (blockSelectionAllowed) onSelect();
          return;
        }
        if (toolMode === 'cursor') {
          onCursorBlockClick(e, annotation);
        } else {
          onSelect();
        }
      }}
      onDblClick={(e) => {
        e.cancelBubble = true;
        if (!isPrimaryBlockPointerAction({
          button: e.evt.button,
          ctrlKey: e.evt.ctrlKey,
          platform: navigator.platform,
        })) return;
        if (formOpeningAllowed) onDoubleClick(e);
      }}
      onMouseEnter={(e) => {
        const isHandle = e.target.name() === 'resize-handle';
        if (!isHandle && interaction !== 'none' && !isDragging && !isResizing) {
          e.target.getStage().container().style.cursor = interaction === 'marquee' ? 'crosshair' : 'grab';
        }
        setIsHovered(true);
      }}
      onMouseLeave={(e) => {
        e.target.getStage().container().style.cursor = 'default';
        setIsHovered(false);
      }}
    >
      {/* Main block */}
      <Rect
        width={finalWidth}
        height={height}
        fill={getBlockColor()}
        stroke={COLORS.BLOCK_BORDER}
        strokeWidth={DEFAULT_STROKE_WIDTH}
      />
      
      {/* Stripe pattern overlay for completed blocks */}
      {completed && stripePattern && (
        <Rect
          width={finalWidth}
          height={height}
          fillPatternImage={stripePattern}
          fillPatternRepeat="repeat"
        />
      )}
      
      {/* Inner selection border - only when selected */}
      {isSelected && (
        <Rect
          x={1.5}
          y={1.5}
          width={finalWidth - 3}
          height={height - 3}
          fill="transparent"
          stroke={COLORS.BLOCK_BORDER}
          strokeWidth={3}
        />
      )}

      {isCopied && (
        <Rect
          x={2}
          y={2}
          width={Math.max(0, finalWidth - 4)}
          height={Math.max(0, height - 4)}
          fill="transparent"
          stroke="#3467d6"
          strokeWidth={3}
          dash={[8, 5]}
          listening={false}
        />
      )}

      {isPasted && (
        <Rect
          x={3}
          y={3}
          width={Math.max(0, finalWidth - 6)}
          height={Math.max(0, height - 6)}
          fill="transparent"
          stroke="#18a957"
          strokeWidth={3}
          shadowColor="#18a957"
          shadowBlur={10}
          shadowOpacity={0.55}
          listening={false}
        />
      )}
      
      {/* Text */}
      <Text
        x={5}
        y={5}
        text={displayText}
        fontSize={DEFAULT_FONT_SIZE}
        fontFamily="Raleway" // Use Raleway font for headers
        fill={isMarkedForDeletion ? "#666666" : COLORS.ANNOTATION_TEXT}
        width={finalWidth - 10}
      />
      
      {/* Delete button */}
      {!isViewMode && !interactionDisabled && toolMode === 'pencil' && isHovered && <DeleteButton width={finalWidth} onDelete={(e) => {
        onDelete();
        e.target.getStage().container().style.cursor = 'default';
      }} />}
      
      {/* Resize handle */}
      {resizeEnabled &&
      <ResizeHandle 
        width={finalWidth}
        height={height} 
        onStartResize={startResizing} 
      />}
    </Group>
  );
});

export default AnnotationBlock;
